import { PenTool, Printer, Factory, Wrench, Puzzle, Eye, Crosshair, Maximize2, Zap } from "lucide-react";

export const COMPANY = {
  name: "MSG — Mark Signs & Graphics",
  short: "MSG",
  address: "9751 McGillivray Rd, Woodbridge, Ontario, Canada L4H 4Z1",
  phone: "647-986-4690",
  phoneRaw: "+16479864690",
  email: "Info@msgsigns.ca",
  hours: "Mon – Fri · 8:00 AM – 6:00 PM EST",
};

const IMG = {
  neon: "https://images.unsplash.com/photo-1496449903678-68ddcb189a24?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  retail: "https://images.pexels.com/photos/8635704/pexels-photo-8635704.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  archSign: "https://images.pexels.com/photos/30096221/pexels-photo-30096221.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  press: "https://images.unsplash.com/photo-1503694978374-8a2fa686963a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  fabrication: "https://images.unsplash.com/photo-1695603414659-4d573962b517?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  machinery: "https://images.unsplash.com/photo-1581508512961-0e3b9524db40?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};
export { IMG };

export const SOLUTIONS = [
  {
    id: "pop-pos",
    num: "01",
    title: "POP / POS Displays",
    color: "var(--brand-orange)",
    desc: "High-impact point-of-purchase and point-of-sale displays engineered to convert foot traffic into sales. From countertop units to full floor displays.",
    features: ["Countertop & floor displays", "Corrugate & permanent builds", "Brand-compliant finishing", "Retail-ready packing & rollout"],
    image: IMG.retail,
  },
  {
    id: "banners",
    num: "02",
    title: "Banners & Graphics",
    color: "var(--brand-magenta)",
    desc: "Grand-format banners, wall murals, and environmental graphics printed with vivid, fade-resistant inks for indoor and outdoor campaigns.",
    features: ["Grand-format printing up to 16 ft", "Mesh, vinyl & fabric substrates", "Hemming, grommets & finishing", "Weather-rated outdoor media"],
    image: IMG.press,
  },
  {
    id: "retail",
    num: "03",
    title: "Retail Displays",
    color: "var(--brand-cyan)",
    desc: "Custom retail environments, shelf systems, and merchandising fixtures that put your product in the spotlight and elevate the in-store experience.",
    features: ["Custom merchandising fixtures", "Shelf & aisle systems", "Acrylic, wood & metal builds", "National rollout programs"],
    image: IMG.retail,
  },
  {
    id: "backlit",
    num: "04",
    title: "Backlit Displays",
    color: "var(--brand-lime)",
    desc: "LED-illuminated lightboxes, channel letters, and backlit fabric systems that keep your brand glowing day and night.",
    features: ["LED lightboxes & SEG frames", "Channel letters & halo lighting", "Energy-efficient LED systems", "Day/night colour management"],
    image: IMG.neon,
  },
  {
    id: "window-glass",
    num: "05",
    title: "Window & Glass Graphics",
    color: "var(--brand-orange)",
    desc: "Frosted films, perforated vinyl, and full-window takeovers that turn glass into a storytelling canvas without sacrificing daylight.",
    features: ["Frosted & etched-look films", "Perforated one-way vision vinyl", "Privacy & decorative films", "Precision contour cutting"],
    image: IMG.archSign,
  },
];

export const SERVICES = [
  {
    id: "design",
    num: "01",
    title: "Design",
    icon: PenTool,
    color: "var(--brand-orange)",
    desc: "In-house designers and structural engineers translate your brand into production-ready artwork, prototypes, and 3D renderings before a single sheet is printed.",
    points: ["Concept & brand adaptation", "Structural / packaging engineering", "3D visualization & prototyping", "Print-ready file preparation"],
    image: IMG.fabrication,
  },
  {
    id: "printing",
    num: "02",
    title: "Printing",
    icon: Printer,
    color: "var(--brand-magenta)",
    desc: "Grand-format UV, latex, and dye-sublimation printing with calibrated colour management for consistent, brand-accurate output at any scale.",
    points: ["UV flatbed & roll-to-roll", "Dye-sublimation fabric printing", "G7-calibrated colour workflow", "Rigid & flexible substrates"],
    image: IMG.press,
  },
  {
    id: "fabrication",
    num: "03",
    title: "Fabrication",
    icon: Factory,
    color: "var(--brand-cyan)",
    desc: "CNC routing, laser cutting, welding, and finishing — all under one roof. We build displays and signage that survive the real world.",
    points: ["CNC routing & laser cutting", "Metal, acrylic & wood work", "LED integration & wiring", "Powder coating & finishing"],
    image: IMG.machinery,
  },
  {
    id: "installation",
    num: "04",
    title: "Installation",
    icon: Wrench,
    color: "var(--brand-lime)",
    desc: "Certified installation crews handle permits, site surveys, and safe installs across the Greater Toronto Area and beyond.",
    points: ["Site surveys & permits", "Certified install crews", "Bucket truck & crane service", "Maintenance & service calls"],
    image: IMG.archSign,
  },
  {
    id: "custom",
    num: "05",
    title: "Custom Solutions",
    icon: Puzzle,
    color: "var(--brand-orange)",
    desc: "Odd sizes, tight deadlines, never-been-done builds — our project managers turn complex requirements into turnkey deliverables.",
    points: ["Turnkey project management", "Multi-location rollouts", "Rush & phased production", "Storage & logistics support"],
    image: IMG.neon,
  },
];

export const PROJECTS = [
  { id: 1, title: "Flagship Storefront Illumination", category: "Backlit", client: "Retail Flagship — Toronto", desc: "Full-façade channel letters with halo LED lighting and backlit fabric lightboxes.", image: IMG.neon, specs: ["Channel letters — 48\" height", "6500K LED halo system", "Aluminum + acrylic faces"] },
  { id: 2, title: "National POP Rollout", category: "Displays", client: "CPG Brand — 240 locations", desc: "Corrugate floor displays designed, printed, and shipped to 240 stores in 3 weeks.", image: IMG.retail, specs: ["240-unit national rollout", "Litho-laminated corrugate", "Retail-ready packout"] },
  { id: 3, title: "Architectural Wayfinding Program", category: "Architectural", client: "Commercial Plaza — Woodbridge", desc: "Exterior monument signage and interior wayfinding system with brushed aluminum finishes.", image: IMG.archSign, specs: ["Monument + pylon signage", "Brushed aluminum & acrylic", "Illuminated address numerals"] },
  { id: 4, title: "Grand Format Campaign Banners", category: "Graphics", client: "Event Series — GTA", desc: "Weather-rated mesh banners and building wraps for a season-long outdoor campaign.", image: IMG.press, specs: ["16 ft grand-format print", "Wind-rated mesh vinyl", "Welded hems + grommets"] },
  { id: 5, title: "Retail Environment Refresh", category: "Retail", client: "Fashion Retailer — Vaughan", desc: "Custom merchandising fixtures, window graphics, and interior brand environments.", image: IMG.retail, specs: ["Custom acrylic fixtures", "Frosted window film", "Interior wall murals"] },
  { id: 6, title: "Precision Fabrication Series", category: "Displays", client: "Tech Brand — Mississauga", desc: "CNC-routed dimensional logos and backlit lobby signage with seamless finishes.", image: IMG.fabrication, specs: ["CNC-routed dimensional logo", "Edge-lit acrylic panels", "Museum-grade finishing"] },
];

export const PROJECT_CATEGORIES = ["All", "Retail", "Architectural", "Displays", "Backlit", "Graphics"];

export const CLIENTS = [
  { name: "Northline Retail", sector: "Retail Chain" },
  { name: "Vaughan Motors Group", sector: "Automotive" },
  { name: "Maple & Oak Hospitality", sector: "Restaurants" },
  { name: "GTA Health Partners", sector: "Healthcare" },
  { name: "Summit Properties", sector: "Real Estate" },
  { name: "TrueNorth Fitness", sector: "Fitness" },
  { name: "Lakeshore Foods", sector: "CPG / Grocery" },
  { name: "Meridian Events", sector: "Events" },
];

export const EQUIPMENT = [
  { name: "Grand-Format UV Flatbed", spec: "Up to 10 ft × 16 ft rigid boards, 6-colour + white", color: "var(--brand-orange)" },
  { name: "Roll-to-Roll Latex Printer", spec: "126\" wide, outdoor-rated media, unattended runs", color: "var(--brand-magenta)" },
  { name: "CNC Router & Digital Cutter", spec: "Oscillating knife + router, camera-registered contour cutting", color: "var(--brand-cyan)" },
  { name: "Dye-Sublimation Fabric Line", spec: "SEG backlit fabric, flags & soft signage", color: "var(--brand-lime)" },
  { name: "Laser Cutter & Metal Shop", spec: "Acrylic, aluminum composite, welding & polishing", color: "var(--brand-orange)" },
  { name: "LED Assembly Station", spec: "Module layout, wiring, transformers & testing", color: "var(--brand-cyan)" },
];

export const MANIFESTO = [
  { num: "01", title: "VISION", icon: Eye, body: "Every sign starts as a business goal. We design for the sightline, the store aisle, and the split-second decision — not just for the proof sheet." },
  { num: "02", title: "PRECISION", icon: Crosshair, body: "Calibrated colour, CNC-cut edges, and millimetre tolerances. What leaves our Woodbridge facility matches the render — every time, at any quantity." },
  { num: "03", title: "SCALE", icon: Maximize2, body: "One prototype or a 500-store rollout. Our production floor, logistics, and install crews are built to scale without losing craft." },
  { num: "04", title: "IMPRESSION", icon: Zap, body: "The finished piece has one job: make your brand impossible to ignore. Day or night, indoors or out — we make it glow." },
];

export const STATS = [
  { value: 15, suffix: "+", label: "Years of Combined Craft" },
  { value: 1200, suffix: "+", label: "Projects Delivered" },
  { value: 500, suffix: "+", label: "Store Rollouts Handled" },
  { value: 48, suffix: "hr", label: "Rush Turnaround Available" },
];
